import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { AuthProvider, useAuth } from './core/auth'
import { Shell } from './ui/Shell'
import { SignIn } from './screens/SignIn'
import { Home } from './screens/Home'
import { Roles } from './screens/Roles'
import { Accounts } from './modules/admin/Accounts'
import { Directory } from './modules/directory/Directory'
import { NorseTerm, NorseTermPage } from './modules/norseterm/NorseTerm'
import { NorseTermProvider } from './modules/norseterm/NorseTermData'
import { FlexPd, FlexPdSetup } from './modules/flexpd/FlexPd'
import { NewRequest, RequestPage, ReviewQueue } from './modules/flexpd/Requests'
import { Tracker } from './modules/flexpd/Tracker'
import { Collaborate } from './modules/flexpd/Collaborate'
import { FpdStaff } from './modules/flexpd/FpdStaff'
import { Loading, Denied } from './ui/States'

function Gate({ children }: { children: React.ReactNode }) {
  const { status } = useAuth()
  if (status === 'loading') return <Loading label="Signing you in" />
  if (status !== 'ready') return <SignIn />
  return <>{children}</>
}

/** Staff pages. A student who types the address gets a plain no, not an error. */
function StaffOnly({ children }: { children: React.ReactNode }) {
  const { profile } = useAuth()
  return profile ? <>{children}</> : <Denied what="this page" />
}

/** The old address for the accounts screen, kept so a bookmark still lands. */
function ToAccounts() {
  const { search } = useLocation()
  return <Navigate to={`/admin/accounts${search}`} replace />
}

function Require({ tool, what, children }: { tool: string; what: string; children: React.ReactNode }) {
  const { canUseTool } = useAuth()
  return canUseTool(tool) ? <>{children}</> : <Denied what={what} />
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        {/* Above the shell, so the NorseTerm screen and the checklist in the
            rail share one set of listeners. */}
        <NorseTermProvider>
        <Shell>
          <Gate>
            <Routes>
              <Route path="/" element={<Home />} />
              {/* Gates itself — shows only the scopes you may administer. */}
              <Route path="/admin/accounts" element={<StaffOnly><Accounts /></StaffOnly>} />
              <Route path="/admin/staff" element={<ToAccounts />} />
              {/* Not admin screens — every staff member gets these. */}
              <Route path="/roles" element={<StaffOnly><Roles /></StaffOnly>} />
              <Route path="/directory" element={<StaffOnly><Directory /></StaffOnly>} />
              <Route path="/norseterm" element={<Require tool="norseterm" what="Norse Term"><NorseTerm /></Require>} />
              <Route path="/norseterm/:page" element={<Require tool="norseterm" what="Norse Term"><NorseTermPage /></Require>} />
              <Route path="/flex-pd" element={<Require tool="flexpd" what="Flex PD"><FlexPd /></Require>} />
              <Route path="/flex-pd/setup" element={<Require tool="flexpd" what="Flex PD"><FlexPdSetup /></Require>} />
              <Route path="/flex-pd/new" element={<Require tool="flexpd" what="Flex PD"><NewRequest /></Require>} />
              <Route path="/flex-pd/staff" element={<Require tool="flexpd" what="Flex PD"><FpdStaff /></Require>} />
              <Route path="/flex-pd/collaborate" element={<Require tool="flexpd" what="Flex PD"><Collaborate /></Require>} />
              <Route path="/flex-pd/tracker" element={<Require tool="flexpd" what="Flex PD"><Tracker /></Require>} />
              <Route path="/flex-pd/review" element={<Require tool="flexpd" what="Flex PD"><ReviewQueue /></Require>} />
              <Route path="/flex-pd/:id" element={<Require tool="flexpd" what="Flex PD"><RequestPage /></Require>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Gate>
        </Shell>
        </NorseTermProvider>
      </BrowserRouter>
    </AuthProvider>
  )
}
